export function parseConfigValue(raw) {
  if (raw == null) return null;

  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  }

  return raw;
}

export function summarizeCredentialPreview(raw) {
  if (typeof raw !== 'string') return null;

  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (trimmed.length <= 4) return '••••';
  return `••••${trimmed.slice(-4)}`;
}

export function normalizeIntegrationConfig(raw) {
  const parsed = parseConfigValue(raw);

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return {};
  }

  const {
    apiKey: _legacyApiKey,
    api_key: _legacyApiKeySnake,
    connectedAt: _rawConnectedAt,
    connected_at: _rawConnectedAtSnake,
    connectedBy: _rawConnectedBy,
    connected_by: _rawConnectedBySnake,
    credentialPreview: _rawCredentialPreview,
    credential_preview: _rawCredentialPreviewSnake,
    hasCredential: _rawHasCredential,
    has_credential: _rawHasCredentialSnake,
    ...rest
  } = parsed;

  const legacyApiKey = parsed.apiKey ?? parsed.api_key ?? null;
  const connectedAt = parsed.connectedAt ?? parsed.connected_at ?? null;
  const connectedBy = parsed.connectedBy ?? parsed.connected_by ?? null;
  const enabled = parsed.enabled ?? parsed.connected ?? false;
  const credentialPreview = parsed.credentialPreview ?? parsed.credential_preview ?? summarizeCredentialPreview(legacyApiKey);
  const hasCredential = parsed.hasCredential ?? parsed.has_credential ?? Boolean(credentialPreview || legacyApiKey);
  const configured = parsed.configured ?? parsed.connected ?? Boolean(hasCredential);

  return {
    ...rest,
    apiKey: null,
    credentialPreview,
    hasCredential: Boolean(hasCredential),
    connectedAt,
    connectedBy,
    enabled: Boolean(enabled),
    configured: Boolean(configured),
  };
}

export function buildConnectedIntegrationConfig({ apiKey, connectedBy, extra = {}, existingConfig } = {}) {
  const existing = normalizeIntegrationConfig(existingConfig);
  const credentialPreview = summarizeCredentialPreview(apiKey) || existing.credentialPreview || 'Configured';

  return {
    ...extra,
    enabled: true,
    configured: true,
    apiKey: null,
    hasCredential: true,
    credentialPreview,
    connectedAt: new Date().toISOString(),
    connectedBy: connectedBy || existing.connectedBy || null,
  };
}

export function buildDisconnectedIntegrationConfig(extra = {}) {
  return {
    ...extra,
    enabled: false,
    configured: false,
    apiKey: null,
    hasCredential: false,
    credentialPreview: null,
    connectedAt: null,
    connectedBy: null,
  };
}

export function isIntegrationConnected(raw) {
  const cfg = normalizeIntegrationConfig(raw);
  return Boolean(cfg.enabled && (cfg.hasCredential || cfg.configured));
}

export function hasExplicitIntegrationState(raw) {
  const parsed = parseConfigValue(raw);
  return Boolean(parsed && typeof parsed === 'object' && !Array.isArray(parsed) && Object.keys(parsed).length > 0);
}
